import type { FCWithChildren, SupportedLanguage } from '@pbk-web/types';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEditorChrome } from '../../core/context/EditorChromeContext';
import { useFeatureConfig } from '../../core/context/FeaturesContext';

type MultiLanguage = {
  currentLanguage: SupportedLanguage;
  setCurrentLanguage: (language: SupportedLanguage) => void;
  /** The current language's stored text (`translations[language][translationKey]`). */
  currentValue: string;
  /** Writes the current language's text back through `features.multiLanguage.onChange`. */
  setCurrentValue: (value: string) => void;
};

const MultiLanguageContext = createContext<MultiLanguage | null>(null);

/** The multi-language state, or `null` when the feature is off. */
export const useMultiLanguage = (): MultiLanguage | null => useContext(MultiLanguageContext);

/**
 * Tracks the language being edited (starting on the UI language) and reads/writes its translation.
 * - Goes back to the UI language whenever the editor stops being active, like Froala and `TranslatableInput`.
 * - Registered as the multi-language feature's `Provider`, so it only exists while the feature is on.
 */
export const MultiLanguageProvider: FCWithChildren = ({ children }) => {
  const { i18n } = useTranslation();
  const config = useFeatureConfig('multiLanguage');
  const { focused } = useEditorChrome();
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(i18n.language as SupportedLanguage);

  useEffect(() => {
    if (!focused) setCurrentLanguage(i18n.language as SupportedLanguage);
  }, [focused, i18n.language]);

  const translations = config?.translations;
  const translationKey = config?.translationKey ?? '';
  const onTranslationsChange = config?.onChange;

  const setCurrentValue = useCallback(
    (value: string) => {
      if (!translations) return;
      onTranslationsChange?.({ ...translations, [currentLanguage]: { ...translations[currentLanguage], [translationKey]: value } });
    },
    [currentLanguage, onTranslationsChange, translationKey, translations],
  );

  const value = useMemo<MultiLanguage>(
    () => ({
      currentLanguage,
      setCurrentLanguage,
      currentValue: translations?.[currentLanguage]?.[translationKey] ?? '',
      setCurrentValue,
    }),
    [currentLanguage, setCurrentValue, translationKey, translations],
  );

  return <MultiLanguageContext.Provider value={value}>{children}</MultiLanguageContext.Provider>;
};
